'use client'

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { PanelRight, Pin, Square, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './dialog'
import {
  Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from './sheet'
import {
  useOverlayMode, useSidebarKeepOpen, useSidebarWidth, type OverlayMode,
} from './dialog-sidebar-mode'
import { OverlayCoachmark, useOverlayCoachmarks } from './dialog-sidebar-coachmark'
import { useViewportTier } from './use-viewport-tier'

export type { OverlayMode } from './dialog-sidebar-mode'
export { useOverlayMode, useSidebarKeepOpen, useSidebarWidth } from './dialog-sidebar-mode'

/** CSS variable que a app raiz pode consumir pra comprimir conteúdo quando keepOpen. */
export const SIDEBAR_PUSH_VAR = '--ds-sidebar-push'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** Conteúdo extra do footer, renderizado à esquerda do ModeToggle. */
  footer?: ReactNode
  /** Modo inicial antes de localStorage ser lido. Default: 'sidebar'. */
  defaultMode?: OverlayMode
  /** Width default em pixels (sidebar). Default 400. */
  defaultSidebarWidth?: number
  /** Classes Tailwind aplicadas ao container do modo dialog. */
  dialogWidthClass?: string
  /** Esconde o ModeToggle. */
  hideModeToggle?: boolean
  /** Esconde o coachmark. */
  hideCoachmark?: boolean
  /** Esconde o toggle de "manter aberto". */
  hideKeepOpen?: boolean
  children: ReactNode
}

export function DialogSidebar({
  open,
  onOpenChange,
  title,
  description,
  footer,
  defaultMode = 'sidebar',
  defaultSidebarWidth = 400,
  dialogWidthClass = 'sm:max-w-[500px]',
  hideModeToggle,
  hideCoachmark,
  hideKeepOpen,
  children,
}: Props) {
  const [storedMode, setMode] = useOverlayMode(defaultMode)
  const [storedKeepOpen, setKeepOpen] = useSidebarKeepOpen()
  const [width, setWidth] = useSidebarWidth(defaultSidebarWidth)
  const coachmarks = useOverlayCoachmarks(open)
  // Tiers: mobile <768 forca dialog; medium 768-1079 permite sidebar mas nao
  // permite fixar (sem espaço lateral confortável); large >=1080 permite tudo.
  const tier = useViewportTier()
  const isMobile = tier === 'mobile'
  const isLarge = tier === 'large'
  const mode: OverlayMode = isMobile ? 'dialog' : storedMode
  // keepOpen só efetivo em large; viewport medium descarta o stored sem perder
  // (volta sozinho quando resize pra >=1080).
  const keepOpen = isLarge ? storedKeepOpen : false

  // CSS variable pra app comprimir conteúdo quando keepOpen+sidebar+open.
  // Importante: só seta+limpa quando shouldPush=true. Em Strict mode o cleanup
  // sem condição causava remove→set→remove visíveis (flicker).
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (!(open && mode === 'sidebar' && keepOpen)) return
    const root = document.documentElement
    root.style.setProperty(SIDEBAR_PUSH_VAR, `${width}px`)
    return () => { root.style.removeProperty(SIDEBAR_PUSH_VAR) }
  }, [open, mode, keepOpen, width])

  const handleModeChange = (m: OverlayMode) => {
    if (m === mode) return
    setMode(m)
    if (!hideCoachmark) coachmarks.onModeChanged()
  }

  // keepOpen só relevante em large desktop. Em medium, esconde o Pin.
  const showKeepOpenControl = !hideKeepOpen && isLarge
  const toggle = (hideModeToggle || isMobile) ? null : (
    <OverlayCoachmark
      variant={hideCoachmark ? null : coachmarks.variant}
      onDismiss={coachmarks.dismiss}
    >
      <ModeToggle
        mode={mode}
        onChange={handleModeChange}
        keepOpen={showKeepOpenControl ? keepOpen : undefined}
        onKeepOpenChange={showKeepOpenControl ? setKeepOpen : undefined}
      />
    </OverlayCoachmark>
  )

  const footerContent = (footer || toggle) ? (
    <div className="flex items-center justify-between w-full gap-2">
      <div className="flex-1 min-w-0">{footer}</div>
      {toggle}
    </div>
  ) : null

  // Lockout de 300ms após abrir pra ignorar interactions outside espúrias
  // (race entre o click do trigger e o handler global do Radix).
  const justOpenedRef = useRef(false)
  useEffect(() => {
    if (!open) return
    justOpenedRef.current = true
    const t = setTimeout(() => { justOpenedRef.current = false }, 300)
    return () => clearTimeout(t)
  }, [open])

  if (mode === 'sidebar') {
    // modal=true por default (sem keepOpen): focus trap + clique fora fecha (UX padrão de sheet).
    // modal=false só quando keepOpen=true: usuário pode interagir com a página enquanto a sidebar está fixada.
    return (
      <Sheet open={open} onOpenChange={onOpenChange} modal={!keepOpen}>
        <SheetContent
          side="right"
          className="flex flex-col p-0 gap-0"
          style={{ width: `${width}px`, maxWidth: '95vw' }}
          showCloseButton={false}
          onInteractOutside={(e) => {
            if (justOpenedRef.current || keepOpen) e.preventDefault()
          }}
          onEscapeKeyDown={keepOpen ? (e) => e.preventDefault() : undefined}
        >
          <ResizeHandle width={width} defaultWidth={defaultSidebarWidth} onChange={setWidth} />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 size-7 z-10"
            onClick={() => onOpenChange(false)}
            aria-label="Fechar"
          >
            <X className="size-4" />
          </Button>
          <SheetHeader className="px-4 pt-3 pb-2 pr-10 gap-1">
            <SheetTitle>{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
          {/* pl-4 mantém alinhamento com header; pr-1 mínimo pra scroll quase na borda. */}
          <div className="flex-1 min-h-0 overflow-y-auto flex flex-col pl-4 pr-1 py-2">{children}</div>
          {footerContent && (
            <SheetFooter className="!p-0 !mt-0 border-t border-border/40 px-3 py-2">
              {footerContent}
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // Mobile full-screen: ocupa 100dvw x 100dvh, sem border/radius/padding
          // exterior. md+ volta ao dialog centralizado padrao (breakpoint
          // alinhado com useIsMobile = 768px).
          'flex flex-col p-0 md:p-6 gap-0 md:gap-4',
          'h-[100dvh] w-screen max-w-none rounded-none border-0',
          'top-0 left-0 translate-x-0 translate-y-0',
          'md:top-[50%] md:left-[50%] md:translate-x-[-50%] md:translate-y-[-50%]',
          'md:h-[80vh] md:w-full md:border md:rounded-lg',
          dialogWidthClass,
        )}
      >
        <DialogHeader className="px-4 pt-3 pb-2 md:p-0">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-4 md:px-0 py-2">{children}</div>
        {footerContent && (
          <DialogFooter className="px-4 py-3 md:p-0 border-t md:border-0 md:flex-row md:items-center">{footerContent}</DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface ResizeHandleProps {
  width: number
  defaultWidth: number
  onChange: (w: number) => void
}

export function ResizeHandle({ width, defaultWidth, onChange }: ResizeHandleProps) {
  const [dragging, setDragging] = useState(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(width)

  const handleDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    startXRef.current = e.clientX
    startWidthRef.current = width
    setDragging(true)
  }, [width])

  const handleDoubleClick = useCallback(() => {
    onChange(defaultWidth)
  }, [defaultWidth, onChange])

  useEffect(() => {
    if (!dragging) return
    const handleMove = (e: PointerEvent) => {
      const delta = startXRef.current - e.clientX
      onChange(startWidthRef.current + delta)
    }
    const handleUp = () => setDragging(false)
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    document.body.style.cursor = 'ew-resize'
    document.body.style.userSelect = 'none'
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [dragging, onChange])

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Redimensionar painel (duplo-clique para resetar)"
      title="Arraste para redimensionar · duplo-clique para resetar"
      onPointerDown={handleDown}
      onDoubleClick={handleDoubleClick}
      className={cn(
        'group absolute left-0 top-0 bottom-0 w-2.5 cursor-ew-resize z-50 flex items-center justify-center',
        'before:content-[""] before:absolute before:left-1/2 before:top-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
        'before:h-12 before:w-[3px] before:rounded-full before:bg-border before:transition-colors',
        'hover:before:bg-primary/70 hover:bg-primary/10 transition-colors',
        dragging && 'bg-primary/20 before:!bg-primary',
      )}
    />
  )
}

interface ModeToggleProps {
  mode: OverlayMode
  onChange: (m: OverlayMode) => void
  /** Mostra também o toggle de "manter aberto" (apenas relevante em sidebar). */
  keepOpen?: boolean
  onKeepOpenChange?: (v: boolean) => void
  className?: string
  ariaLabel?: string
}

export function ModeToggle({ mode, onChange, keepOpen, onKeepOpenChange, className, ariaLabel = 'Modo de exibição' }: ModeToggleProps) {
  const showKeepOpen = mode === 'sidebar' && onKeepOpenChange != null
  return (
    <div
      className={cn(
        'inline-flex items-center rounded-md border border-border p-0.5 gap-0.5',
        className,
      )}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn('h-7 w-7', mode === 'dialog' && 'bg-muted')}
        aria-pressed={mode === 'dialog'}
        onClick={() => onChange('dialog')}
        title="Modal"
      >
        <Square className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn('h-7 w-7', mode === 'sidebar' && 'bg-muted')}
        aria-pressed={mode === 'sidebar'}
        onClick={() => onChange('sidebar')}
        title="Painel lateral"
      >
        <PanelRight className="h-3.5 w-3.5" />
      </Button>
      {showKeepOpen && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('h-7 w-7', keepOpen && 'bg-primary/15 text-primary')}
          aria-pressed={!!keepOpen}
          onClick={() => onKeepOpenChange!(!keepOpen)}
          title={keepOpen ? 'Soltar (fecha ao clicar fora)' : 'Fixar painel (comprime a página)'}
        >
          <Pin className={cn('h-3.5 w-3.5', keepOpen && 'rotate-45')} />
        </Button>
      )}
    </div>
  )
}
